import { YStack } from 'tamagui';

import PSButton from '@/components/ui/PSButton';
import PSText from './ui/PSText';

/**
 * TierNotice — the block a `TicketTier` shows at its foot when the stepper
 * isn't the answer (molecule).
 *
 * A recessed card: a bold heading, a line of body copy, and at most one CTA.
 * One shape for every reason a tier can be blocked (log in, get a membership,
 * you've hit your limit) because every one of them IS this shape — they differ
 * only in strings and in whether there's anything to press. This replaces the
 * `Upsell` / `DownloadAppNudge` pair, which were the same three tokens twice
 * over; the six-reason payload would have made it six.
 *
 * Render-only, and deliberately copy-free: unlike `SecretMenuCard`, which owns
 * its per-state caption, this block has no state to key off. `TicketTier` owns
 * the reason → copy map, because the reason is what makes the words correct.
 *
 * `action` is one object rather than a `buttonLabel` + `onPress` pair so the
 * no-CTA case is a single absent prop, and a label can't arrive without a
 * handler to run (a dead button in a dead-end state is exactly the bug this
 * shape is prone to). The one state that legitimately has no action is a
 * `limit_reached` on the highest plan — nothing left to buy, so nothing to press.
 */
type TierNoticeProps = {
  title: string;
  body: string;
  /** Omitted when there is nothing the reader can do about it. */
  action?: { label: string; onPress: () => void };
  surface?: 'light' | 'dark';
};

export default function TierNotice({
  title,
  body,
  action,
  surface = 'light',
}: TierNoticeProps) {
  const dark = surface === 'dark';
  return (
    <YStack
      borderWidth="$borderWidth.hairline"
      borderColor={dark ? '$transparent' : '$borderColor'}
      rounded="$base"
      bg={dark ? '$panelTranslucent' : '$backgroundSecondary'}
      p="$md"
      gap="$md"
    >
      <PSText.H2 color={dark ? '$textWhite' : '$textDark'}>{title}</PSText.H2>
      {body ? (
        <PSText.Body2 color={dark ? '$textWhiteMuted' : '$textDark'}>
          {body}
        </PSText.Body2>
      ) : null}
      {action ? (
        <PSButton title={action.label} onPress={action.onPress} />
      ) : null}
    </YStack>
  );
}
